
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import { ReportState, CustomReportModal, Types, GraphTypes } from "@/store/reports/types";
import vSelect3 from "vselect3";
import { AxiosResponse } from 'axios';
import CustomReportsApi, { CustomScriptVaildResponse, CustomReportResponse} from "@/services/api.customReports.service";
import ConfirmationModal from "@/components-shared/ConfirmationModal.vue"
import { UserPermissions } from '@/store/types';

const ReportsStore = namespace("reports");

@Component({
    components: {
			"v-select3": vSelect3,
            "confirmation-modal": ConfirmationModal,
    }
})
export default class CustomReports extends Vue {
    @Getter getPermissions: UserPermissions;

    @ReportsStore.Mutation setCustomReportModalShown: (customReportModal: CustomReportModal) => void;
    @ReportsStore.Action fetchReportTypes: any;
    @ReportsStore.State((state: ReportState) => state.CustomReportModal) private customReportModal: CustomReportModal;

    @ReportsStore.Getter("getReportTypes") reportTypes;

    readonly customReportRequestStatusSuccessful: number = 200;
    private checkState = "Ready";

    private showConfirmationModal: boolean = false;
    private confirmationModalMessage: string = null;
    private confirmModalOkButtonText: string = null;
    private confirmModalCancelButtonText: string = null;
    private confirmModalDeletion: boolean = false;
    private confirmModalTitle: string = "Confirmation Modal";

    private reportData: Types = {
        category: null,
        drilldown: null,
        endpoint: "",
        insightID: 0,
        templatable: false,
        name: "",
        customScript: "",
        defaultGraphType: 0,
        customTitle: null,
        title: ""
    };

    private GraphTypesArray: Object[] =  [
            {Label:'Line Chart', id: GraphTypes.Line},
            {Label:'Column Chart', id: GraphTypes.Column},
            {Label:'Table', id: GraphTypes.Table},
            {Label:'Pie Chart', id: GraphTypes.Pie},
            {Label:'Donut Chart', id: GraphTypes.Donut},
    ]

    @Watch('customReportModal.shown')
    onModalShown(): void{
        this.getReportData(this.customReportModal.insightID);
        this.resetCheckState();
    }

    private getReportData(reportID: number): void{    
        let reportType = null; 
        for (var categoryCount = 0; categoryCount < this.reportTypes.length; categoryCount++){
            for (var typeCount = 0; typeCount < this.reportTypes[categoryCount].types.length; typeCount++){
                if (this.reportTypes[categoryCount].types[typeCount].insightID == reportID){
                    reportType = this.reportTypes[categoryCount].types[typeCount];

                    this.reportData = {
                        category: reportType.category,
                        drilldown: reportType.drilldown,
                        endpoint: reportType.endpoint,
                        insightID: reportType.insightID,
                        templatable: reportType.templatable,
                        name:reportType.title,
                        customScript: reportType.customScript,
                        defaultGraphType: reportType.defaultGraphType,
                        customTitle: reportType.customTitle,
                        title: reportType.title
                    };      
                }
            }
            if (reportType != null){
                break;
            }
        }

        if (reportType == null){
            this.reportData = {
                category: null,
                drilldown: null,
                endpoint: "",
                insightID: 0,
                templatable: false,
                name: "",
                customScript: "",
                defaultGraphType: 0,
                customTitle: null,
                title: ""
            }
        }
    } 
    
    public closeCustomReportModal() {
		this.setCustomReportModalShown({shown: false, insightID:0, isShared: false});
	}

    private get editCustomReport(): boolean{
        const reportID = this.customReportModal.insightID;
        if (reportID > 0){
            return true;
        } else {
            return false;
        }
    }    

    private cancel(){
        this.closeCustomReportModal();
    }

    private get reportCategories(): String[] { 
        const reportTypes = this.reportTypes;
        var categories = [];
        reportTypes.forEach(types => {
            if (types.title !== "Reports Created" && types.title !== "Shared With You" && types.title !== "Favorites") {
                categories.push(types.title);
            }
        });
        return categories;
    }

    private async resetCheckState(){
        this.checkState = "Ready";
    }

    private async checkCustomScript(){
        let customScript: string = this.reportData.customScript;
        this.checkState = 'Checking';

        let response: AxiosResponse<CustomScriptVaildResponse> = await CustomReportsApi.customScriptValid(customScript);
        if (response && response.data && response.status === this.customReportRequestStatusSuccessful) {
            if (response.data.customScriptValid == true){
                this.checkState = 'Successful';
            } 
            else {
                this.checkState = 'Fail';
                if (response.data.error != null){
                    this.openConfirmationModal("Parse Script Error", response.data.error, false, "", "OK")
                }
            }
        } 
        else {
            this.checkState = 'Fail';
            this.openConfirmationModal("Parse Script Error", "Error: No resonse from server.", false, "", "OK")
        }
    }

    private async submitCustomReport(){
        let response: AxiosResponse<CustomReportResponse>

        //Create an endpoint from the name the user has given
        this.reportData.endpoint = "/" + this.reportData.name.replace(/\s/g, '');
        //Check if call is an update or create call
        if (this.reportData.insightID == 0){
            response = await CustomReportsApi.createCustomReport(this.reportData);
        } 
        else {
            response = await CustomReportsApi.updateCustomReport(this.reportData);
        }
        
        if (response && response.data && response.status === this.customReportRequestStatusSuccessful) {
            if (response.data.successful == true){
                //If response is successful close modal and open custom Report
                this.closeCustomReportModal();
                await this.fetchReportTypes();
                
                if (this.$router.currentRoute.path == `/reports${ response.data.customReport.endpoint }`) {
                    this.$router.go(0);
                } 
                else {
                    this.$router.push(`/reports${response.data.customReport.endpoint}`);
                }
            } 
            else {
                this.openConfirmationModal("Custom Report Error", response.data.error, false, "", "OK")
            }
        } 
        else {
            this.openConfirmationModal("Custom Report Error", "Error: No resonse from server.", false, "", "OK");
        }
    }

    private openConfirmationModal(title: string, message: string, deletion: boolean, okText: string, cancelText: string) {
        this.confirmModalDeletion = deletion;
        this.confirmModalOkButtonText = okText;
        this.confirmModalCancelButtonText = cancelText;
        this.confirmationModalMessage = message;
        this.confirmModalTitle = title;
        this.showConfirmationModal = true;
    }

    private closeConfirmationModal() {     
        this.showConfirmationModal = false;
        this.confirmModalDeletion = false;
        this.confirmModalOkButtonText = "";
        this.confirmModalCancelButtonText = "OK";
        this.confirmationModalMessage = "";
        this.confirmModalTitle = "";
    }

    private checkDeleteCustomReport(){
        let message: string = "Are you sure you want to delete '" + this.reportData.name + "' custom report?"; 
        this.openConfirmationModal("Delete Custom Report", message, true, "DELETE", "Cancel")
    }

    private async deleteCustomReport(){
        let response: AxiosResponse<CustomReportResponse>      
        response = await CustomReportsApi.deleteCustomReport(this.reportData.insightID);
        if (response && response.data && response.status === this.customReportRequestStatusSuccessful) {
            if (response.data.successful == true){
                //If response is successful close modal 
                this.closeConfirmationModal();
                this.closeCustomReportModal();
                await this.fetchReportTypes();
            } 
            else {
                this.openConfirmationModal("Delete Custom Report Error", response.data.error, false, "", "OK")
            }
        } 
        else {
            this.openConfirmationModal("Delete Custom Report Error", "Error: No resonse from server.", false, "", "OK");
        }
    }
}
